/**
 * Pagina de Login para QOps.
 */
import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  login,
  LoginCredentials,
  startGoogleOAuth,
  startMicrosoftOAuth,
  fetchEnvironments,
  EnvironmentInfo,
} from '../lib/auth';

export default function LoginPage() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Multi-ambiente
  const [environments, setEnvironments] = useState<EnvironmentInfo[]>([]);
  const [selectedEnv, setSelectedEnv] = useState<EnvironmentInfo | null>(null);
  const [multiEnv, setMultiEnv] = useState(false);

  useEffect(() => {
    fetchEnvironments().then((data) => {
      if (data.multi_env && data.environments.length > 1) {
        setEnvironments(data.environments);
        setMultiEnv(true);
        // Seleccionar el primario por defecto
        const primary = data.environments.find((e) => e.is_primary) || data.environments[0];
        setSelectedEnv(primary);
      }
    });
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const creds: LoginCredentials = {
        ...credentials,
        env: selectedEnv?.key,
      };
      const result = await login(creds, selectedEnv || undefined);

      if (result.success) {
        if (!result.user) {
          setError('Error obteniendo datos del usuario. Intente nuevamente.');
          return;
        }
        const dest = result.user.tipo === 'CLIENTE' ? '/portal' : '/';
        navigate(dest);
      } else {
        setError(result.error || 'Credenciales incorrectas');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Error de conexion');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full">
        {/* Logo y titulo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-blue-600 mb-2">QOps</h1>
          <p className="text-gray-600">
            Plataforma de Operacion y Soporte
          </p>
        </div>

        {/* Formulario de login */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">
            Iniciar Sesion
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Environment selector (solo si multi-env) */}
            {multiEnv && environments.length > 1 && (
              <div>
                <label
                  htmlFor="environment"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Ambiente
                </label>
                <div className="relative">
                  <select
                    id="environment"
                    value={selectedEnv?.key || ''}
                    onChange={(e) => {
                      const env = environments.find((env) => env.key === e.target.value);
                      setSelectedEnv(env || null);
                    }}
                    className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    {environments.map((env) => (
                      <option key={env.key} value={env.key}>
                        {env.label}
                      </option>
                    ))}
                  </select>
                  {/* Color dot indicator */}
                  {selectedEnv && (
                    <span
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full"
                      style={{ backgroundColor: selectedEnv.color }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials({ ...credentials, email: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="usuario@ejemplo.com"
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tu contraseña"
              />
            </div>

            {/* Boton de submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>

          {/* Separador */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">o continuar con</span>
            </div>
          </div>

          {/* OAuth buttons */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={startGoogleOAuth}
              className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700 font-medium transition duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </button>

            <button
              type="button"
              onClick={startMicrosoftOAuth}
              className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700 font-medium transition duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21">
                <rect fill="#F25022" x="1" y="1" width="9" height="9" />
                <rect fill="#7FBA00" x="11" y="1" width="9" height="9" />
                <rect fill="#00A4EF" x="1" y="11" width="9" height="9" />
                <rect fill="#FFB900" x="11" y="11" width="9" height="9" />
              </svg>
              Microsoft
            </button>
          </div>

          {/* Links adicionales */}
          <div className="mt-6 text-center text-sm text-gray-600">
            <a
              href="#"
              className="text-blue-600 hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 text-center text-sm text-gray-500">
          QOps v1.0.0 - Quantum Operations
        </div>
      </div>
    </div>
  );
}
